package top.wcpe.allincore.dsl.ir.canvas

import top.wcpe.allincore.dsl.ir.codec.ByteArrayIrWriter
import top.wcpe.allincore.dsl.ir.codec.IrByteWriter
import kotlin.jvm.JvmStatic

/**
 * draw 指令序列 → 字节数组的编码器。
 *
 * 从原 `DrawBlob.encode` 拆出（M11.x 重构）；输出字节布局完全一致。
 * 底层由 [ByteArrayIrWriter] 承载（大端、动态增长），与旧 `ByteBuffer` 逐字节相同。
 */
object DrawBlobEncoder {

    /** 将指令序列编码为字节数组。 */
    @JvmStatic
    fun encode(instructions: List<Instruction>): ByteArray {
        if (instructions.size > 0xFFFF) {
            throw IllegalArgumentException("draw 指令数超过 65535: ${instructions.size}")
        }
        // ByteArrayIrWriter 动态增长，无需预算 size；u16 count 打头。
        val w = ByteArrayIrWriter()
        w.writeShort(instructions.size)
        for (ins in instructions) {
            encodeInstruction(w, ins)
        }
        return w.toByteArray()
    }

    private fun encodeInstruction(w: IrByteWriter, ins: Instruction) {
        val op = ins.opcode
        val ops = ins.operandsRaw()
        w.writeByte(op.toInt())
        when (op) {
            DrawOpcodes.OP_FOR_END, DrawOpcodes.OP_IF_END -> {
                // 无后续负载
            }
            DrawOpcodes.OP_VAL_ASSIGN -> {
                val slot = literalInt(ops[0])
                w.writeByte(slot and 0xFF)
                encodeOperand(w, ops[1])
            }
            DrawOpcodes.OP_FOR_BEGIN -> {
                val slot = literalInt(ops[0])
                val iter = literalInt(ops[1])
                val bodyLen = literalInt(ops[2])
                w.writeByte(slot and 0xFF)
                w.writeShort(iter)
                w.writeShort(bodyLen)
            }
            DrawOpcodes.OP_IF_BEGIN -> {
                encodeOperand(w, ops[0])
                val thenLen = literalInt(ops[1])
                val elseLen = literalInt(ops[2])
                w.writeShort(thenLen)
                w.writeShort(elseLen)
            }
            DrawOpcodes.OP_POLYGON -> {
                // u8 vertexCount + 2N×Operand + colorOperand
                val vertexCount = (ops.size - 1) / 2
                w.writeByte(vertexCount and 0xFF)
                for (o in ops) {
                    encodeOperand(w, o)
                }
            }
            DrawOpcodes.OP_PATH -> {
                // u8 vertexCount + 2N×Operand + widthOperand + colorOperand + u8 closed
                val vertexCount = (ops.size - 2) / 2
                w.writeByte(vertexCount and 0xFF)
                for (o in ops) {
                    encodeOperand(w, o)
                }
                w.writeByte(if (ins.isPathClosed) 1 else 0)
            }
            DrawOpcodes.OP_PATH_BUILD -> {
                // u8 cmdCount + cmdCount×(u8 cmdKind + N×Operand) + u8 modeByte
                //   + widthOperand + colorOperand
                val kinds = ins.pathBuildKindsRaw()
                    ?: throw IllegalStateException("OP_PATH_BUILD 缺少 pathBuildKinds 元数据")
                w.writeByte(kinds.size and 0xFF)
                var operandCursor = 0
                for (k in kinds) {
                    w.writeByte(k.toInt())
                    val n = DrawBlobSpec.pathNodeOperandCount(k)
                    for (i in 0 until n) {
                        encodeOperand(w, ops[operandCursor++])
                    }
                }
                w.writeByte(ins.pathBuildMode.toInt())
                // 末尾依次为 width + color（共 2 个 operand）
                encodeOperand(w, ops[operandCursor++])
                encodeOperand(w, ops[operandCursor])
            }
            else -> {
                for (o in ops) {
                    encodeOperand(w, o)
                }
            }
        }
    }

    private fun encodeOperand(w: IrByteWriter, op: Operand) {
        when (op) {
            is Operand.Literal -> {
                w.writeByte(DrawOpcodes.OPERAND_LITERAL.toInt())
                w.writeDouble(op.value)
            }
            is Operand.Expr -> {
                val exprBytes = op.getBytes()
                w.writeByte(DrawOpcodes.OPERAND_EXPR.toInt())
                w.writeShort(exprBytes.size)
                w.writeBytes(exprBytes)
            }
            is Operand.Bytes -> {
                // v4 B.5：u8 kind(0x03) + u16 byteLen + raw bytes
                val raw = op.value
                w.writeByte(DrawOpcodes.OPERAND_BYTES.toInt())
                w.writeShort(raw.size)
                w.writeBytes(raw)
            }
            is Operand.Str -> {
                val bytes = op.value.encodeToByteArray()
                w.writeByte(DrawOpcodes.OPERAND_STR.toInt())
                w.writeShort(bytes.size)
                w.writeBytes(bytes)
            }
        }
    }

    /** 从内部元数据 Operand（必为 Literal）取整数值。 */
    private fun literalInt(op: Operand): Int {
        if (op !is Operand.Literal) {
            throw IllegalStateException("控制流元数据必须为 LITERAL 操作数")
        }
        return op.value.toInt()
    }
}
